/**
 * WebSocket Service
 *
 * Manages WebSocket connection for real-time content updates from the backend.
 * Uses STOMP protocol over SockJS for reliable WebSocket communication.
 *
 * Features:
 * - Automatic reconnection with exponential backoff
 * - Message type routing to registered handlers
 * - Feature flag support (can be disabled via environment variable)
 * - Connection status tracking
 * - Error handling and logging
 *
 * Message Types Supported:
 * - PAGE_UPDATED
 * - PAGE_DELETED
 * - PRODUCT_UPDATED
 * - PRODUCT_DELETED
 * - CATEGORY_UPDATED
 * - CATEGORY_DELETED
 */

import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import featureFlags from '../utils/featureFlags';

const WS_URL = process.env.REACT_APP_WS_URL || 'http://localhost:8080/ws/content-updates';

class WebSocketService {
  constructor() {
    this.client = null;
    this.connected = false;
    this.connecting = false;  // Track connection in progress
    this.handlers = new Map();
    this.connectionCallbacks = [];  // Support multiple callbacks
  }

  /**
   * Connect to the WebSocket server
   *
   * @param {Function} onConnected - Callback when connection is established
   * @param {Function} onDisconnected - Callback when connection is lost
   * @param {Function} onError - Callback when an error occurs
   */
  connect(onConnected, onDisconnected, onError) {
    // Check if WebSocket is enabled via feature flag
    if (!featureFlags.useWebSocketUpdates()) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[WebSocket] Disabled by feature flag (REACT_APP_ENABLE_WEBSOCKET=false)');
      }
      return;
    }

    // Store callbacks for this caller
    if (onConnected || onDisconnected || onError) {
      this.connectionCallbacks.push({ onConnected, onDisconnected, onError });
    }

    // Already connected - notify caller immediately
    if (this.connected) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[WebSocket] Already connected');
      }
      onConnected?.();
      return;
    }

    // Connection already in progress - callbacks stored, will be notified when connected
    if (this.connecting) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[WebSocket] Connection already in progress, queuing callback');
      }
      return;
    }

    // Mark as connecting to prevent duplicate connections
    this.connecting = true;

    if (process.env.NODE_ENV === 'development') {
      console.log(`[WebSocket] Connecting to ${WS_URL}...`);
    }

    // Create STOMP client
    this.client = new Client({
      // Use SockJS for WebSocket fallback support
      webSocketFactory: () => new SockJS(WS_URL),

      // Reconnection settings
      reconnectDelay: 5000,  // 5 seconds initial delay
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,

      // Connection established
      onConnect: () => {
        this.connected = true;
        this.connecting = false;
        console.log('[WebSocket] Connected successfully');

        // Notify all registered callbacks
        this.connectionCallbacks.forEach(cb => cb.onConnected?.());

        // Subscribe to content updates topic
        this.client.subscribe('/topic/content-updates', (message) => {
          try {
            const parsedMessage = JSON.parse(message.body);
            this.handleMessage(parsedMessage);
          } catch (err) {
            console.error('[WebSocket] Failed to parse message:', err);
            console.error('[WebSocket] Raw message:', message.body);
          }
        });
      },

      // Connection lost
      onDisconnect: () => {
        this.connected = false;
        this.connecting = false;
        console.log('[WebSocket] Disconnected');
        this.connectionCallbacks.forEach(cb => cb.onDisconnected?.());
      },

      // STOMP protocol error
      onStompError: (frame) => {
        const errorMessage = frame.headers['message'] || 'Unknown error';
        console.error('[WebSocket] STOMP error:', errorMessage);
        console.error('[WebSocket] Error frame:', frame);
        this.connectionCallbacks.forEach(cb => cb.onError?.(errorMessage));
      },

      // WebSocket error
      onWebSocketError: (event) => {
        console.error('[WebSocket] WebSocket error:', event);
        this.connecting = false;
        this.connectionCallbacks.forEach(cb => cb.onError?.('WebSocket connection error'));
      },

      // Debug logging (only in development)
      debug: process.env.NODE_ENV === 'development'
        ? (str) => console.log('[WebSocket Debug]', str)
        : () => {} // Disable in production
    });

    // Activate the client (starts connection)
    this.client.activate();
  }

  /**
   * Handle incoming message by routing to registered handlers
   *
   * @param {Object} message - Parsed message from server
   * @param {string} message.type - Message type (e.g., "PAGE_UPDATED")
   * @param {Object} message.payload - Message payload
   * @param {string} message.timestamp - ISO 8601 timestamp
   */
  handleMessage(message) {
    const { type, payload, timestamp } = message;

    if (process.env.NODE_ENV === 'development') {
      console.log(`[WebSocket] Received ${type}:`, payload);
    }

    // Find and execute handler for this message type
    const handler = this.handlers.get(type);
    if (handler) {
      try {
        handler(payload, timestamp);
      } catch (err) {
        console.error(`[WebSocket] Error in handler for ${type}:`, err);
      }
    } else {
      if (process.env.NODE_ENV === 'development') {
        console.warn(`[WebSocket] No handler registered for message type: ${type}`);
      }
    }
  }

  /**
   * Register a handler for a specific message type
   *
   * @param {string} messageType - Message type to handle (e.g., "PAGE_UPDATED")
   * @param {Function} handler - Handler function (payload, timestamp) => void
   */
  on(messageType, handler) {
    if (typeof handler !== 'function') {
      console.error(`[WebSocket] Handler for ${messageType} must be a function`);
      return;
    }

    this.handlers.set(messageType, handler);

    if (process.env.NODE_ENV === 'development') {
      console.log(`[WebSocket] Registered handler for ${messageType}`);
    }
  }

  /**
   * Unregister a handler for a specific message type
   *
   * @param {string} messageType - Message type to unregister
   */
  off(messageType) {
    this.handlers.delete(messageType);

    if (process.env.NODE_ENV === 'development') {
      console.log(`[WebSocket] Unregistered handler for ${messageType}`);
    }
  }

  /**
   * Disconnect from the WebSocket server
   */
  disconnect() {
    if (this.client) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[WebSocket] Disconnecting...');
      }
      this.client.deactivate();
      this.client = null;
      this.connected = false;
      this.connecting = false;
      this.connectionCallbacks = [];
    }
  }

  /**
   * Check if currently connected
   *
   * @returns {boolean} - True if connected
   */
  isConnected() {
    return this.connected;
  }

  /**
   * Get connection status details
   *
   * @returns {Object} - Connection status
   */
  getStatus() {
    return {
      connected: this.connected,
      url: WS_URL,
      enabled: featureFlags.useWebSocketUpdates(),
      handlerCount: this.handlers.size
    };
  }
}

// Export singleton instance
const websocketService = new WebSocketService();
export default websocketService;
